import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Plus, Loader2, UserPlus, ArrowRight, Filter, Pencil, Trash2, MoreVertical, Settings } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Import custom hooks
import { usePipelineManagement } from '../hooks/usePipelineManagement';
import { usePipelineClients } from '../hooks/usePipelineClients';
import { usePipelineDragDrop } from '../hooks/usePipelineDragDrop';

// Import components
import PipelineFormDialog from '../components/PipelineFormDialog';
import PipelineStageColumn from '../components/PipelineStageColumn';

const TEAM_MEMBERS = ['Emily Chen', 'David Park', 'Lisa Wang'];

const Pipelines = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Pipeline management hook
  const {
    pipelines,
    selectedPipeline,
    setSelectedPipeline,
    createPipelineDialog,
    setCreatePipelineDialog,
    editPipelineDialog,
    setEditPipelineDialog,
    editPipeline,
    setEditPipeline,
    deletePipelineDialog,
    setDeletePipelineDialog,
    pipelineToDelete,
    newPipeline,
    setNewPipeline,
    fetchPipelines,
    handleCreatePipeline,
    openEditPipelineDialog,
    handleUpdatePipeline,
    openDeletePipelineDialog,
    handleDeletePipeline,
    addStage,
    removeStage,
    updateStage,
    addTaskToStage,
    removeTaskFromStage,
    updateTask
  } = usePipelineManagement();

  // Pipeline clients hook
  const {
    pipelineClients,
    allClients,
    assigneeFilter,
    setAssigneeFilter,
    addClientDialog,
    setAddClientDialog,
    selectedClientToAdd,
    selectedAssigneeToAdd,
    setSelectedAssigneeToAdd,
    forSpouse,
    setForSpouse,
    selectedClientHasSpouse,
    deleteClientDialog,
    setDeleteClientDialog,
    clientToDelete,
    fetchAllClients,
    fetchPipelineClients,
    handleClientSelectForAdd,
    handleAddClientToPipeline,
    openDeleteClientDialog,
    handleDeleteClientFromPipeline,
    handleTaskToggle
  } = usePipelineClients(selectedPipeline);

  // Drag and drop hook
  const {
    dragOverStage,
    moveDialog,
    movingClient,
    sourceStage,
    targetStage,
    pendingTasks,
    checkedTasks,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    toggleTask,
    handleCompleteAndMove,
    cancelMove
  } = usePipelineDragDrop(selectedPipeline, fetchPipelineClients);

  // Initial data fetch
  useEffect(() => {
    fetchPipelines();
    fetchAllClients();
  }, []);

  // Fetch clients when pipeline or filter changes
  useEffect(() => {
    if (selectedPipeline) {
      fetchPipelineClients();
    }
  }, [selectedPipeline, allClients, assigneeFilter]);

  // Loading state
  const loading = pipelines.length === 0 && !selectedPipeline;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="pipelines-title">Pipelines</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreatePipelineDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Pipeline
          </Button>
        </div>
      </div>

      {/* Pipeline Tabs and Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {pipelines.map(pipeline => (
            <div key={pipeline._id} className="flex items-center">
              <Button
                variant={selectedPipeline?._id === pipeline._id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPipeline(pipeline)}
                className="rounded-r-none"
              >
                {pipeline.name}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={selectedPipeline?._id === pipeline._id ? "default" : "outline"}
                    size="icon" 
                    className="h-8 w-6 rounded-l-none border-l-0"
                  >
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEditPipelineDialog(pipeline)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Pipeline
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="text-red-600"
                    onClick={() => openDeletePipelineDialog(pipeline)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Pipeline
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Assignee Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="w-40 h-8 text-sm">
                <SelectValue placeholder="All Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team</SelectItem>
                {TEAM_MEMBERS.map(member => (
                  <SelectItem key={member} value={member}>{member}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Client Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setAddClientDialog(true)}
            className="gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Pipeline Board */}
      {selectedPipeline && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {selectedPipeline.stages.map(stage => (
            <PipelineStageColumn
              key={stage.id}
              stage={stage}
              clients={pipelineClients[stage.id] || []}
              dragOverStage={dragOverStage}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragStart={handleDragStart}
              onTaskToggle={handleTaskToggle}
              onDeleteClient={openDeleteClientDialog}
            />
          ))}
        </div>
      )}

      {/* Move Client Dialog */}
      <Dialog open={moveDialog} onOpenChange={cancelMove}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Tasks Before Moving</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              <span className="font-medium">{movingClient?.displayName}</span>
              <ArrowRight className="h-4 w-4" />
              <Badge style={{ backgroundColor: targetStage?.color }} className="text-white">
                {targetStage?.name}
              </Badge>
            </div>
            
            <p className="text-sm text-gray-600">
              Please complete all tasks in <strong>{sourceStage?.name}</strong> stage before moving:
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {pendingTasks.map((task) => (
                <div 
                  key={task.id} 
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    checkedTasks.includes(task.id) ? 'bg-green-50 border-green-200' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleTask(task.id)}
                >
                  <Checkbox
                    id={task.id}
                    checked={checkedTasks.includes(task.id)}
                    onCheckedChange={() => toggleTask(task.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={task.id}
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {task.name}
                    </label>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={handleCompleteAndMove} 
                className="flex-1"
                disabled={checkedTasks.length < pendingTasks.length}
              >
                Complete & Move ({checkedTasks.length}/{pendingTasks.length})
              </Button>
              <Button variant="outline" onClick={cancelMove}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client to Pipeline Dialog */}
      <Dialog open={addClientDialog} onOpenChange={setAddClientDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Client to {selectedPipeline?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Client</Label>
              <Select value={selectedClientToAdd} onValueChange={handleClientSelectForAdd}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {allClients.map(client => (
                    <SelectItem key={client._id} value={client._id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedClientHasSpouse && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="forSpouse"
                  checked={forSpouse}
                  onCheckedChange={setForSpouse}
                />
                <Label htmlFor="forSpouse">This is for spouse</Label>
              </div>
            )}

            <div className="space-y-2">
              <Label>Assign To (optional)</Label>
              <Select value={selectedAssigneeToAdd} onValueChange={setSelectedAssigneeToAdd}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {TEAM_MEMBERS.map(member => (
                    <SelectItem key={member} value={member}>{member}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddClientDialog(false)}>Cancel</Button>
            <Button onClick={handleAddClientToPipeline}>Add to Pipeline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Pipeline Dialog */}
      <PipelineFormDialog
        open={createPipelineDialog}
        onOpenChange={setCreatePipelineDialog}
        pipeline={newPipeline}
        setPipeline={setNewPipeline}
        onSave={handleCreatePipeline}
        onAddStage={addStage}
        onRemoveStage={removeStage}
        onUpdateStage={updateStage}
        onAddTaskToStage={addTaskToStage}
        onRemoveTaskFromStage={removeTaskFromStage}
        onUpdateTask={updateTask}
        isEdit={false}
      />

      {/* Edit Pipeline Dialog */}
      <PipelineFormDialog
        open={editPipelineDialog}
        onOpenChange={setEditPipelineDialog}
        pipeline={editPipeline}
        setPipeline={setEditPipeline}
        onSave={handleUpdatePipeline}
        onAddStage={addStage}
        onRemoveStage={removeStage}
        onUpdateStage={updateStage}
        onAddTaskToStage={addTaskToStage}
        onRemoveTaskFromStage={removeTaskFromStage}
        onUpdateTask={updateTask}
        isEdit={true}
      />

      {/* Delete Pipeline Dialog */}
      <Dialog open={deletePipelineDialog} onOpenChange={setDeletePipelineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Pipeline</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete the pipeline "<strong>{pipelineToDelete?.name}</strong>"? 
              All client assignments to this pipeline will also be removed. This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeletePipelineDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeletePipeline}>Delete Pipeline</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Client from Pipeline Dialog */}
      <Dialog open={deleteClientDialog} onOpenChange={setDeleteClientDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Client from Pipeline</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to remove "<strong>{clientToDelete?.displayName}</strong>" from this pipeline? 
              {clientToDelete?.forSpouse && <span className="text-pink-600 ml-1">(Spouse)</span>}
              <br />
              This will delete all progress for this client in the pipeline.
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteClientDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClientFromPipeline}
              data-testid="confirm-delete-client-btn"
            >
              Remove from Pipeline
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pipelines;
